import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Flame, Zap, Video, MessageCircle, Users, User, ChevronRight, X, Sparkle, Maximize2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Single source of truth for the local-storage flag — bump the version string
// any time the tour content materially changes and you want returning users
// to see it again.
const TOUR_STORAGE_KEY = "spark.tour.completed.v3";

export function hasSeenTour() {
  try { return localStorage.getItem(TOUR_STORAGE_KEY) === "true"; } catch { return false; }
}
export function markTourSeen() {
  try { localStorage.setItem(TOUR_STORAGE_KEY, "true"); } catch { /* noop */ }
}
export function resetTour() {
  try { localStorage.removeItem(TOUR_STORAGE_KEY); } catch { /* noop */ }
}

const STEPS = [
  {
    icon: Sparkle,
    accent: "#C9A961",
    title: "Welcome to Spark",
    body: "Quiet signals, real connections. Here's the 60-second tour — swipe through it once and you'll know exactly where to look for everything.",
    cta: "Show me around",
  },
  {
    icon: Flame,
    accent: "#B95F73",
    title: "Discover",
    body: "Swipe right (♥ Spark) to like, left (✕ Pass) to skip. On laptops you can use ← / → arrow keys, Z to rewind your last decision. Verified profiles wear a gold badge — no catfish.",
    cta: "Next",
  },
  {
    icon: Maximize2,
    accent: "#8FE0C5",
    title: "Tap any photo to magnify",
    body: "On Discover, in the chat list, on profiles, in Reacted — tap any photo and it expands to fullscreen. Swipe (or click ← / →) to browse all of someone's photos. Tap outside or swipe down to close.",
    cta: "Got it",
  },
  {
    icon: Zap,
    accent: "#C9A961",
    title: "Tonight",
    body: "Free tonight? Light a beacon — tell the city what kind of vibe (coffee, walk, drinks…) and people open to meeting will see you. Beacons auto-expire in 24h.",
    cta: "Next",
  },
  {
    icon: Video,
    accent: "#B95F73",
    title: "Moments",
    body: "Short videos that say more than any bio. Post to your page, to the public feed, or both — you pick. Pin your best one to the top of your profile.",
    cta: "Next",
  },
  {
    icon: MessageCircle,
    accent: "#C9A961",
    title: "Chats & Voice",
    body: "Tap any match to chat. Hold the mic icon for voice notes. Tap the phone for in-app voice or video calls — your number stays private.",
    cta: "Next",
  },
  {
    icon: Users,
    accent: "#B95F73",
    title: "Groups",
    body: "40 themed group chats — from Coffee Snobs to Sunday Movie Club. Meet people through shared interests, not just photos.",
    cta: "Next",
  },
  {
    icon: User,
    accent: "#C9A961",
    title: "Your Profile",
    body: "Verify with a selfie to earn the gold badge. Edit your prompts, add a video bio, pin your best Moments. Tap the persona switcher to alternate between your weekend self and your work self.",
    cta: "Start using Spark",
  },
];

export default function WelcomeTour({ open, onClose }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const finish = () => {
    markTourSeen();
    onClose?.();
    nav("/discover");
  };
  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else finish();
  };

  const s = STEPS[step];
  const Icon = s?.icon || Sparkle;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[150] bg-[#0E0F12]/96 backdrop-blur-md flex items-center justify-center px-5"
          data-testid="welcome-tour"
        >
          {/* Skip button — top right */}
          <button
            type="button"
            onClick={finish}
            data-testid="tour-skip"
            className="absolute top-[max(env(safe-area-inset-top),16px)] right-4 flex items-center gap-1 text-[#8C867C] hover:text-[#F0E6D2] text-[11px] font-bold tracking-[0.18em] uppercase active:scale-95 transition"
          >
            Skip <X className="w-3.5 h-3.5" />
          </button>

          {/* Progress dots */}
          <div className="absolute top-[max(env(safe-area-inset-top),16px)] left-1/2 -translate-x-1/2 flex gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${i === step ? "w-6 bg-[#F0E6D2]" : i < step ? "w-1.5 bg-[#C9A961]" : "w-1.5 bg-white/20"}`}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -24, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 250, damping: 26 }}
              className="w-full max-w-sm flex flex-col items-center text-center"
            >
              <div
                className="w-24 h-24 rounded-3xl flex items-center justify-center mb-7"
                style={{ background: `${s.accent}22`, border: `1px solid ${s.accent}66` }}
              >
                <Icon className="w-10 h-10" style={{ color: s.accent }} strokeWidth={1.4} />
              </div>
              <h2 className="font-display text-3xl text-[#F0E6D2] leading-tight tracking-tight mb-3">
                {s.title}
              </h2>
              <p className="text-[#A0A0A8] text-[15px] leading-relaxed mb-10 max-w-xs">
                {s.body}
              </p>

              <button
                type="button"
                onClick={next}
                data-testid={step === STEPS.length - 1 ? "tour-finish" : "tour-next"}
                className="w-full max-w-xs flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#F0E6D2] text-[#16171A] text-sm font-bold tracking-widest uppercase active:scale-[0.98] transition"
              >
                {s.cta}
                <ChevronRight className="w-4 h-4" />
              </button>

              <div className="text-[#5E5C5A] text-[10px] tracking-[0.18em] uppercase mt-6">
                Step {step + 1} of {STEPS.length}
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
